import React from 'react';
import type { FloatingContext, Placement, ReferenceType, UseFloatingProps } from '@floating-ui/react-dom-interactions';
import {
  arrow,
  autoPlacement,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
  useRole,
} from '@floating-ui/react-dom-interactions';

export interface TooltipOptions {
  /**
   * The placement of the tooltip relative to the reference element. See: https://floating-ui.com/docs/computePosition#placement
   */
  placement?: Placement;
  /**
   * Whether the tooltip should initialise with the floating element visible.
   */
  initialOpen?: boolean;
  /**
   * Options for autoplacement. See: https://floating-ui.com/docs/autoPlacement
   */
  autoPlacementOpts?: Parameters<typeof autoPlacement>[0];
  /**
   * A function to manually configure Floating UI's interactions. See https://floating-ui.com/docs/react-dom-interactions
   */
  configureInteractions?: (context: FloatingContext<ReferenceType>) => ReturnType<typeof useInteractions>;
}

/**
 *
 * @param tooltipOptions (optional)
 * @returns
 */
export function useTooltipState({
  initialOpen = false,
  placement,
  autoPlacementOpts,
  configureInteractions,
}: TooltipOptions = {}) {
  const [open, setOpen] = React.useState(initialOpen);
  const arrowRef = React.useRef(null);

  const floatingProps: Partial<UseFloatingProps> = {
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, update, { elementResize: false }),
  };

  if (autoPlacementOpts) {
    floatingProps.middleware = [offset(8), autoPlacement(autoPlacementOpts), arrow({ element: arrowRef })];
  } else {
    floatingProps.middleware = [offset(8), flip(), shift(), arrow({ element: arrowRef })];
  }

  const data = useFloating(floatingProps);

  const context = data.context;

  const interactions = configureInteractions
    ? configureInteractions(context)
    : useInteractions([
        useClick(context),
        useFocus(context),
        useDismiss(context),
        useRole(context, { role: 'tooltip' }),
      ]);

  return React.useMemo(
    () => ({
      open,
      setOpen,
      arrowRef,
      ...interactions,
      ...data,
    }),
    [open, setOpen, interactions, data]
  );
}
export type TooltipState = ReturnType<typeof useTooltipState>;

/**
 *
 * @param {TooltipState} state -
 * @returns {ReturnType<useTooltipPositionRef>}
 */
export function useTooltipPositionRef(state: TooltipState) {
  const positioningRef = React.useRef<HTMLDivElement>(null);
  React.useLayoutEffect(() => {
    if (positioningRef.current == null) return;
    const refElem = positioningRef.current;
    state.reference({
      getBoundingClientRect: () => refElem.getBoundingClientRect(),
      contextElement: positioningRef.current,
    });
  }, [state.reference]);

  return { positioningRef };
}
